import { useRef, useEffect, useState } from "react";
import { useNavigate } from "@/hooks/useNavigate";
import { DonationType, isRecurringLike, useDonationFormStore } from "@/stores";
import { formatCurrency, getCurrencySymbol } from "@/utils/formatters";
import { DetailsStep } from "@/types/Navigation";
import {
  useFloating,
  useDismiss,
  useInteractions,
  useClick,
  offset,
  flip,
  shift,
  arrow,
  FloatingArrow,
  FloatingPortal,
  autoUpdate,
} from "@floating-ui/react";
import { InputCurrency } from "@/components/ui/InputCurrency";
import type { AmountConfig } from "@/types/components/AmountComponent";
import { useTranslation } from "@/hooks/useTranslation";
import classes from "./DisplayAmount.module.scss";

export function DisplayAmount({ config }: { config: AmountConfig }) {
  const amount = useDonationFormStore((state) => state.donationAmount);
  const setAmount = useDonationFormStore((state) => state.setDonationAmount);
  const setHasCustomAmount = useDonationFormStore(
    (state) => state.setHasCustomAmount,
  );
  const donationType = useDonationFormStore((state) => state.donationType);
  const isSubmitted = useDonationFormStore((state) => state.isSubmitted);

  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<string | number>(amount ?? "");
  const [error, setError] = useState<string | null>(null);
  const arrowRef = useRef(null);
  const { visibleDetailsSteps } = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    "one-off-donation-amount-min": oneOffDonationAmountMin,
    "monthly-donation-amount-min": monthlyDonationAmountMin,
    "recurring-donation-amount-min": recurringDonationAmountMin,
  } = config;

  const t = useTranslation("amount");

  const enableEditing =
    (donationType === DonationType.OneOff &&
      !visibleDetailsSteps.includes(DetailsStep.YourDetails)) ||
    (isRecurringLike(donationType) && !isSubmitted);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
    },
    whileElementsMounted: autoUpdate,
    middleware: [offset(10), flip(), shift(), arrow({ element: arrowRef })],
    placement: "top",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const minAmountByType = {
    [DonationType.OneOff]: oneOffDonationAmountMin,
    [DonationType.Monthly]: monthlyDonationAmountMin,
    [DonationType.Recurring]:
      recurringDonationAmountMin ?? monthlyDonationAmountMin,
  };

  const minAmount = donationType
    ? minAmountByType[donationType]
    : oneOffDonationAmountMin;

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleSave = () => {
    const numValue = Number(value);

    if (numValue < minAmount) {
      const errorMessage = t("errors.amount-minimum", {
        minimum: formatCurrency(minAmount),
      }) as string;

      setError(errorMessage);
      inputRef.current?.focus();

      return;
    }

    setHasCustomAmount(true);
    setAmount(numValue);
    setIsOpen(false);
    setError(null);
  };

  useEffect(() => {
    if (isOpen) {
      setValue(amount ?? "");
      setError(null);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [isOpen, amount]);

  return (
    <>
      <span ref={refs.setReference} {...getReferenceProps()}>
        {formatCurrency(amount ?? 0)}
        {enableEditing && (
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className={classes.iconButton}
            aria-label="Edit Amount"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              fill="none"
              viewBox="0 0 13 13"
              aria-hidden="true"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M7.042 11.375h4.333m-3.25-8.667 2.167 2.167m1.177-1.185A1.527 1.527 0 1 0 9.31 1.53L2.081 8.76c-.126.126-.219.28-.27.45l-.716 2.358a.27.27 0 0 0 .337.337l2.358-.715c.17-.052.324-.144.45-.27z"
              />
            </svg>
          </button>
        )}
      </span>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={classes.popover}
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              className={classes.arrow}
            />
            <div className={classes.content}>
              <form
                data-testid="amount-form"
                noValidate
                onSubmit={(ev) => {
                  ev.preventDefault();
                  handleSave();
                }}
                className={classes.form}
              >
                <InputCurrency
                  aria-label={
                    t("donation-amount-input-label", {
                      currency: getCurrencySymbol(),
                    }) as string
                  }
                  ref={inputRef}
                  className={classes.input}
                  value={value}
                  placeholder="0.00"
                  onChange={(value) => {
                    setValue(value);
                    if (error) {
                      setError(null);
                    }
                  }}
                  hasError={!!error}
                  min={minAmount}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter") {
                      handleSave();
                    }
                  }}
                />
                <button type="submit" className={classes.textButton}>
                  Confirm
                </button>
              </form>
              {error && <span className={classes.errorMessage}>{error}</span>}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
